/**
 * @fileoverview Product Analytics - Session Tracking
 * @module features/analytics/sessions
 * 
 * @description
 * Track user sessions with comprehensive context including device info,
 * geographic location, traffic source, and engagement metrics.
 * 
 * @useCases
 * - Monitor active user sessions in real-time
 * - Analyze session duration and depth
 * - Track user journey across pages
 * - Identify traffic sources and campaigns
 * - Segment users by device and location
 * 
 * @metrics
 * - Total sessions
 * - Unique visitors
 * - Average session duration
 * - Pages per session
 * - Bounce rate
 * - New vs returning visitors
 * 
 * @chartData
 * - Line chart: Sessions over time
 * - Pie chart: Traffic source breakdown
 * - Bar chart: Sessions by device type
 * - Geo map: Sessions by country
 * 
 * @example
 * ```typescript
 * const sessions = new Sessions(webId, endpoint, apiKey);
 * 
 * // Start a new session
 * const session = await sessions.start({
 *   userId: 'user_123',
 *   device: { type: 'desktop', browser: 'Chrome' },
 *   source: 'google',
 *   medium: 'organic'
 * });
 * 
 * // End session
 * await sessions.end(session.sessionId);
 * ```
 */

import { FlowGridTransport } from "../../client/transport";
import { getWindow, getDocument, isBrowser, combineTeardowns, Teardown } from "../../utils/browser";
import { 
  Session,
  DeviceInfo, 
  GeoLocation, 
  UTMParams,
  TrafficMetrics,
  TimeSeriesData,
  DateRangeFilter,
  PaginationParams,
  UserIdentity
} from "../../types";

// ============================================================================
// CONFIGURATION TYPES
// ============================================================================

/**
 * Configuration for starting a new session.
 */
export interface SessionStartConfig {
  /** User identity */
  user?: UserIdentity;
  /** Device information */
  device?: DeviceInfo;
  /** Geographic location */
  location?: GeoLocation;
  /** Traffic source */
  source?: string;
  /** Traffic medium */
  medium?: string;
  /** Campaign name */
  campaign?: string;
  /** UTM parameters */
  utm?: UTMParams;
  /** Landing page URL */
  landingPage?: string;
  /** Referrer URL */
  referrer?: string;
  /** Custom properties */
  properties?: Record<string, unknown>;
}

/**
 * Session response from API.
 */
export interface SessionResponse {
  eventName: string;
  properties: Record<string, unknown>;
  response: {
    sessionId: string;
    started: boolean;
    isNewVisitor: boolean;
  };
  timestamp: string;
}

/**
 * Session end response.
 */
export interface SessionEndResponse {
  eventName: string;
  properties: Record<string, unknown>;
  response: {
    sessionId: string;
    duration: number;
    pageViews: number;
    eventCount: number;
  };
  timestamp: string;
}

/**
 * Session analytics data for charts.
 */
export interface SessionAnalyticsData {
  /** Summary metrics */
  summary: {
    totalSessions: number;
    uniqueVisitors: number;
    newVisitors: number;
    returningVisitors: number;
    avgSessionDuration: number;
    avgPagesPerSession: number;
    bounceRate: number;
  };
  /** Sessions time series */
  timeSeries: TimeSeriesData;
  /** Sessions by source */
  bySource: Array<{
    source: string;
    sessions: number;
    percentage: number;
  }>;
  /** Sessions by device */
  byDevice: Array<{
    device: string;
    sessions: number;
    percentage: number;
  }>;
  /** Sessions by country */
  byCountry: Array<{
    country: string;
    countryCode: string;
    sessions: number;
  }>;
}

/**
 * Real-time session data.
 */
export interface RealTimeSessionData {
  /** Currently active sessions */
  activeSessions: number;
  /** Active users in last 5 minutes */
  activeUsers5min: number;
  /** Active users in last 30 minutes */
  activeUsers30min: number;
  /** Sessions by page (real-time) */
  sessionsByPage: Array<{ path: string; count: number }>;
  /** Sessions by country (real-time) */
  sessionsByCountry: Array<{ country: string; count: number }>;
}

// ============================================================================
// SESSIONS CLASS
// ============================================================================

/**
 * Sessions - Track and analyze user sessions.
 * 
 * @extends FlowGridTransport
 * 
 * @description
 * Provides comprehensive session tracking including:
 * - Session lifecycle (start, update, end)
 * - Device and location tracking
 * - Traffic source attribution
 * - Real-time session monitoring
 * - Session replay triggers
 * 
 * @example
 * ```typescript
 * const sessions = new Sessions(webId, endpoint, apiKey);
 * 
 * // Start session on page load
 * const session = await sessions.start({
 *   user: { userId: 'user_123' },
 *   device: { type: 'mobile', os: 'iOS' },
 *   source: 'email',
 *   campaign: 'summer_sale'
 * });
 * 
 * // Get real-time data
 * const realTime = await sessions.getRealTime();
 * console.log(`${realTime.activeSessions} users online`);
 * ```
 */
export class Sessions extends FlowGridTransport {
  /**
   * Starts a new user session.
   * 
   * @param config - Session configuration
   * @returns Promise resolving to session response with sessionId
   * 
   * @example
   * ```typescript
   * const session = await sessions.start({
   *   user: { userId: 'user_123' },
   *   source: 'google',
   *   medium: 'cpc',
   *   campaign: 'brand_terms'
   * });
   * localStorage.setItem('sessionId', session.response.sessionId);
   * ```
   */
  async start(config: SessionStartConfig = {}): Promise<SessionResponse> {
    return await this.init("session_start", {
      type: "session_start",
      user: config.user,
      device: config.device,
      location: config.location,
      source: config.source || config.utm?.utmSource,
      medium: config.medium || config.utm?.utmMedium,
      campaign: config.campaign || config.utm?.utmCampaign,
      utm: config.utm,
      landingPage: config.landingPage || (typeof window !== 'undefined' ? window.location.href : ''),
      referrer: config.referrer || (typeof document !== 'undefined' ? document.referrer : ''),
      ...config.properties,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Updates an existing session with new data.
   * 
   * @param sessionId - Session identifier
   * @param properties - Properties to update
   * @returns Promise resolving to update response
   */
  async update(
    sessionId: string, 
    properties: Record<string, unknown>
  ): Promise<SessionResponse> {
    return await this.init("session_update", {
      type: "session_update",
      sessionId,
      ...properties,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Ends a user session.
   * 
   * @param sessionId - Session identifier to end
   * @returns Promise resolving to session end response with metrics
   * 
   * @example
   * ```typescript
   * window.addEventListener('beforeunload', async () => {
   *   const sessionId = localStorage.getItem('sessionId');
   *   if (sessionId) {
   *     await sessions.end(sessionId);
   *   }
   * });
   * ```
   */
  async end(sessionId: string): Promise<SessionEndResponse> {
    return await this.init("session_end", {
      type: "session_end",
      sessionId,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Tracks a heartbeat to keep session alive.
   * 
   * @param sessionId - Session identifier
   * @returns Promise resolving to heartbeat response
   */
  async heartbeat(sessionId: string): Promise<SessionResponse> {
    return await this.init("session_heartbeat", {
      type: "session_heartbeat",
      sessionId,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Auto-manage a browser session: starts on call, sends a heartbeat on a
   * fixed interval, and ends the session on `pagehide`. The resolved
   * `sessionId` is stored on `sessionStorage` so reloads within the same tab
   * resume the same session.
   *
   * @param config Optional initial config forwarded to {@link Sessions.start}.
   * @param options.heartbeatMs Interval between heartbeats. Default 30s.
   * @returns A teardown function that ends the session and detaches listeners.
   */
  autoTrack(config: SessionStartConfig = {}, options: { heartbeatMs?: number } = {}): Teardown {
    if (!isBrowser()) return () => { /* no-op on the server */ };
    const win = getWindow()!;
    const doc = getDocument()!;
    const heartbeatMs = options.heartbeatMs ?? 30_000;

    const STORE_KEY = "flowgrid:sessionId";
    let sessionId: string | undefined;
    try { sessionId = win.sessionStorage.getItem(STORE_KEY) || undefined; } catch { /* private mode */ }

    const startPromise = sessionId
      ? Promise.resolve(sessionId)
      : this.start(config).then((r) => {
          sessionId = r.response.sessionId;
          try { win.sessionStorage.setItem(STORE_KEY, sessionId); } catch { /* ignore */ }
          return sessionId;
        }).catch(() => undefined);

    const beat = win.setInterval(() => {
      if (sessionId && doc.visibilityState === "visible") void this.heartbeat(sessionId);
    }, heartbeatMs);

    const onHide = () => { if (sessionId) void this.end(sessionId); };
    win.addEventListener("pagehide", onHide);

    return combineTeardowns(
      () => win.clearInterval(beat),
      () => win.removeEventListener("pagehide", onHide),
      () => { void startPromise.then(() => { if (sessionId) void this.end(sessionId); }); },
      () => { try { win.sessionStorage.removeItem(STORE_KEY); } catch { /* ignore */ } },
    );
  }
}

export default Sessions;
